import { effect, inject, Injectable, signal } from '@angular/core';
import { ApiDataAccess, Query, QueryBuilder } from '@softline/core';
import { SalesStatisticsGridDataSource } from '../grid/sales-statistics-grid.data-source';
import { applyActiveFilters } from '../helpers/apply-active-filters.function';
import {
  addDateFilter,
  addPeriodColumns,
  addPeriodFilters,
  addValueColumns,
  splitByPeriod,
  STATISTICS_PREFIX,
  valFromRow,
} from '../helpers/document-type-query.helpers';
import {
  InventoryValues,
  loadInventoryValues,
  resolveInventoryValues,
} from '../helpers/inventory-values';
import { ApiRow } from '../types/api-row';
import { RevenueSeriesRow } from '../types/chart-row';
import { SalesStatisticsQueryService } from './sales-statistics-query.service';

interface LoadResult {
  data: ApiRow[];
}

export interface RevenuePeriodsResult {
  current: RevenueSeriesRow[];
  previous: RevenueSeriesRow[] | null;
}

/** `YYYY-MM` from the year/month group functions — the series' row key. Shared
    with the Lagerwert query so both sides key identically. */
function monthKeyOf(row: ApiRow): string {
  const bucket = row[STATISTICS_PREFIX] as Record<string, unknown> | undefined;
  const year = bucket?.['document_date:year'] as number | undefined;
  const month = bucket?.['document_date:month'] as number | undefined;
  if (year == null || month == null) return '';
  return `${year}-${String(month).padStart(2, '0')}`;
}

@Injectable()
export class RevenueTrendChartService {
  queryService = inject(SalesStatisticsQueryService);
  private dataAccess = inject(ApiDataAccess);

  private cache = new Map<string, RevenuePeriodsResult>();
  /** Lagerwert per month, keyed like `cache` — separate query, own cache. */
  private inventoryCache = new Map<string, Promise<Map<string, InventoryValues>>>();

  readonly loading = signal(false);
  readonly hasLoaded = signal(false);
  readonly isEmpty = signal(false);
  readonly hasError = signal(false);
  readonly chartData = signal<object[]>([]);
  lastParamsKey = '';

  constructor() {
    let lastInvalidationKey: string | null = null;
    effect(() => {
      const key = JSON.stringify([
        this.queryService.activeFilters(),
        this.queryService.dateRange(),
        this.queryService.referencePeriod(),
        this.queryService.documentType(),
        this.queryService.statisticType(),
        this.queryService.refreshTick(),
      ]);
      if (lastInvalidationKey === null) {
        lastInvalidationKey = key;
        return;
      }
      if (lastInvalidationKey === key) return;
      lastInvalidationKey = key;
      this.cache.clear();
      this.inventoryCache.clear();
      this.lastParamsKey = '';
    });
  }

  async loadPeriods(
    primary: { from: string; to: string },
    reference: { from: string; to: string } | null,
    documentType = 'INVOICE',
    statisticType = 'TOTAL',
    signal?: AbortSignal
  ): Promise<RevenuePeriodsResult> {
    // Deliberately without the Lagerwert flag: the value rows never carry the
    // Lagerwert aggregate, so they are identical in both toggle states.
    const cacheKey = JSON.stringify({
      primary,
      reference,
      documentType,
      statisticType,
      activeFilters: this.queryService.activeFilters(),
    });
    const cached = this.cache.get(cacheKey);

    const ds = new SalesStatisticsGridDataSource(
      this.dataAccess,
      documentType,
      statisticType
    );

    const base = new QueryBuilder<ApiRow>()
      .start(0)
      .end(10000)
      .column(`${STATISTICS_PREFIX}.document_date`, { usage: 'group', func: 'year' })
      .column(`${STATISTICS_PREFIX}.document_date`, { usage: 'group', func: 'month' });
    base.sort(`${STATISTICS_PREFIX}.document_date`, 'ASC');
    applyActiveFilters(base, this.queryService);
    const inventoryBase = base.copy();
    addValueColumns(base);

    if (cached)
      return this.#applyInventory(
        cached,
        ds,
        inventoryBase,
        cacheKey,
        primary,
        reference
      );

    let rawCurrent: ApiRow[];
    let rawPrevious: ApiRow[] | null;

    if (reference) {
      const builder = base.copy();
      addPeriodColumns(builder);
      addPeriodFilters(builder, primary, reference);
      const result = (await ds.loadAbortable(
        { query: builder.build() as Query<object> },
        signal
      )) as LoadResult | null;
      const split = splitByPeriod(result?.data ?? []);
      rawCurrent = split.current;
      rawPrevious = split.previous;
    } else {
      const builder = base.copy();
      addDateFilter(builder, primary.from, primary.to);
      const result = (await ds.loadAbortable(
        { query: builder.build() as Query<object> },
        signal
      )) as LoadResult | null;
      rawCurrent = result?.data ?? [];
      rawPrevious = null;
    }

    const result: RevenuePeriodsResult = {
      current: this.#toSeriesRows(rawCurrent),
      previous: rawPrevious ? this.#toSeriesRows(rawPrevious) : null,
    };
    this.cache.set(cacheKey, result);
    return this.#applyInventory(result, ds, inventoryBase, cacheKey, primary, reference);
  }

  /** Loads the Lagerwert per month separately and folds it into both series —
      the value query must never carry the aggregate, or the Lagerwert toggle
      would be able to change the Umsatz numbers. */
  async #applyInventory(
    result: RevenuePeriodsResult,
    ds: SalesStatisticsGridDataSource,
    inventoryBase: QueryBuilder<ApiRow>,
    cacheKey: string,
    primary: { from: string; to: string },
    reference: { from: string; to: string } | null
  ): Promise<RevenuePeriodsResult> {
    if (!this.queryService.inventoryEnabled()) return result;
    const values = await resolveInventoryValues(this.inventoryCache, cacheKey, () =>
      // No caller signal — the promise is shared between loads.
      loadInventoryValues(ds, inventoryBase, monthKeyOf, primary, reference)
    );
    if (!values) return result;
    // Current months resolve against `value`, the comparison months (different
    // keys — other year) against `reference`.
    return {
      current: result.current.map((row) => ({
        ...row,
        inventoryValue: values.get(row.field)?.value ?? 0,
      })),
      previous:
        result.previous?.map((row) => ({
          ...row,
          inventoryValue: values.get(row.field)?.reference ?? 0,
        })) ?? null,
    };
  }

  #toSeriesRows(rows: ApiRow[]): RevenueSeriesRow[] {
    const monthMap = new Map<
      string,
      { revenue: number; wes: number; db: number; inventoryValue: number }
    >();
    for (const o of rows) {
      const key = monthKeyOf(o);
      if (!key) continue;

      const net = valFromRow(o, 'net');
      const cost = valFromRow(o, 'cost');
      const db = valFromRow(o, 'contribution_margin');

      const existing = monthMap.get(key);
      if (existing) {
        existing.revenue += net;
        existing.wes += cost;
        existing.db += db;
      } else {
        // inventoryValue is filled in by #applyInventory.
        monthMap.set(key, { revenue: net, wes: cost, db, inventoryValue: 0 });
      }
    }
    return [...monthMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([field, values]) => ({ field, ...values }));
  }
}
